/**
 * Skill Levels List View
 *
 * Read-only skill levels display with localized names and descriptions.
 * Skill levels are managed via database migrations, not through the admin UI.
 * Includes language filter to view localizations in different languages.
 */

import { useState, useMemo } from 'react';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import { useInitialData, type SkillLevel } from '../../../contexts/InitialDataContext';

/**
 * Get localized name for a skill level
 * Falls back to English if requested language not available
 */
function getSkillLevelLocalizedName(skillLevel: SkillLevel, languageCode: string): string {
  const localization = skillLevel.localizations.find((loc) => loc.language_code === languageCode);
  if (localization) return localization.localized_name;

  // Fallback to English
  const englishLoc = skillLevel.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_name;

  // Fallback to first available
  if (skillLevel.localizations.length > 0) {
    return skillLevel.localizations[0].localized_name;
  }

  return `Level ${skillLevel.id}`;
}

/**
 * Get localized description for a skill level
 * Falls back to English if requested language not available
 */
function getSkillLevelLocalizedDescription(
  skillLevel: SkillLevel,
  languageCode: string
): string | undefined {
  const localization = skillLevel.localizations.find((loc) => loc.language_code === languageCode);
  if (localization) return localization.localized_description;

  // Fallback to English
  const englishLoc = skillLevel.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_description;

  // Fallback to first available
  if (skillLevel.localizations.length > 0) {
    return skillLevel.localizations[0].localized_description;
  }

  return undefined;
}

/**
 * Check if skill level has localization for given language
 */
function hasLocalization(skillLevel: SkillLevel, languageCode: string): boolean {
  return skillLevel.localizations.some((loc) => loc.language_code === languageCode);
}

export function SkillLevelsListView() {
  const [displayLanguage, setDisplayLanguage] = useState<string>('en');

  const { state: initialDataState } = useInitialData();

  const languages = initialDataState.data?.languages ?? [];

  // Sort skill levels by ID
  const sortedSkillLevels = useMemo(() => {
    const skillLevels = initialDataState.data?.skillLevels ?? [];
    return [...skillLevels].sort((a, b) => a.id - b.id);
  }, [initialDataState.data?.skillLevels]);

  if (initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (initialDataState.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading skill levels</p>
          <p className="text-gray-600">{initialDataState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Skill Levels</h1>
          <p className="text-gray-600 mt-2">
            Skill levels (IDs 2-5) with localized names and descriptions. Select a language to view
            localizations.
          </p>
        </div>
      </div>

      {/* Language Filter */}
      <div className="mb-6 flex gap-4">
        <div className="w-48">
          <label
            htmlFor="display-language"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Display Language
          </label>
          <select
            id="display-language"
            value={displayLanguage}
            onChange={(e) => setDisplayLanguage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="display-language-selector"
          >
            {languages
              .filter((lang) => lang.code !== 'zxx')
              .map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Skill Levels Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="skill-levels-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Available Localizations
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedSkillLevels.map((skillLevel) => {
              const hasLoc = hasLocalization(skillLevel, displayLanguage);
              return (
                <tr key={skillLevel.id} data-testid={`skill-level-row-${skillLevel.id}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{skillLevel.id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${hasLoc ? 'text-gray-900' : 'text-gray-500 italic'}`}>
                      {getSkillLevelLocalizedName(skillLevel, displayLanguage)}
                      {!hasLoc && ' (fallback)'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${hasLoc ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                      {getSkillLevelLocalizedDescription(skillLevel, displayLanguage) || (
                        <span className="italic text-gray-400">No description</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {skillLevel.localizations
                        .sort((a, b) => a.language_code.localeCompare(b.language_code))
                        .map((loc) => (
                          <span
                            key={loc.language_code}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              loc.language_code === displayLanguage
                                ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-500'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                            title={loc.localized_name}
                          >
                            {loc.language_code.toUpperCase()}
                          </span>
                        ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {sortedSkillLevels.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg">No skill levels configured in the system.</p>
        </div>
      )}
    </div>
  );
}
